// The shared job queue behind the Jobs Monitoring screen (EPIC-010). Every
// mass-update apply/import across Sales Orders, Work Orders, and Import Work
// Orders queues a job here. Draft only — in-memory, resets on page reload,
// nothing persists. `queueMassUpdateJob` simulates a job actually running so
// the Jobs screen has something to watch.

export const MASS_UPDATE_JOB_TYPES = [
  "Sales Order Update",
  "Work Order Update",
  "Work Order Import",
] as const;
export type MassUpdateJobType = (typeof MASS_UPDATE_JOB_TYPES)[number];

export const MASS_UPDATE_JOB_STATUSES = ["Queued", "Processing", "Completed", "Failed"] as const;
export type MassUpdateJobStatus = (typeof MASS_UPDATE_JOB_STATUSES)[number];

export interface MassUpdateJob {
  id: string;
  jobType: MassUpdateJobType;
  status: MassUpdateJobStatus;
  recordCount: number;
  createdAt: string; // ISO datetime
  errorMessage: string | null;
}

let nextJobSeq = 1000;

function job(
  jobType: MassUpdateJobType,
  status: MassUpdateJobStatus,
  recordCount: number,
  createdAt: string,
  errorMessage: string | null = null,
): MassUpdateJob {
  return { id: `JOB-${nextJobSeq++}`, jobType, status, recordCount, createdAt, errorMessage };
}

// Seeded history so the screen isn't empty on first visit.
const jobs: MassUpdateJob[] = [
  job("Sales Order Update", "Completed", 6, "2026-09-17T14:02:00.000Z"),
  job("Work Order Update", "Completed", 11, "2026-09-17T16:40:00.000Z"),
  job(
    "Work Order Import",
    "Failed",
    4,
    "2026-09-18T09:15:00.000Z",
    "NetSuite rejected 1 of 4 rows: item ATP-9000 does not exist.",
  ),
  job("Sales Order Update", "Completed", 3, "2026-09-18T11:30:00.000Z"),
];

export const massUpdateJobsKeys = {
  all: ["mocks", "mass-update-jobs", "jobs"] as const,
};

export function fetchMassUpdateJobs(): Promise<MassUpdateJob[]> {
  return Promise.resolve(
    [...jobs].sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0)),
  );
}

export function hasActiveMassUpdateJob(jobList: MassUpdateJob[] | undefined): boolean {
  if (!jobList) return false;
  return jobList.some((entry) => entry.status === "Queued" || entry.status === "Processing");
}

// Queues a job, then simulates it moving through the pipeline. The Jobs
// screen picks up each transition by polling (see hasActiveMassUpdateJob),
// not through a pub/sub — there's no backend here to push from.
export function queueMassUpdateJob(jobType: MassUpdateJobType, recordCount: number): MassUpdateJob {
  const created = job(jobType, "Queued", recordCount, new Date().toISOString());
  jobs.push(created);

  setTimeout(() => {
    created.status = "Processing";
  }, 1500);

  setTimeout(() => {
    created.status = "Completed";
  }, 4000);

  return created;
}
